import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Badge from '../components/common/Badge';
import VoiceInput from '../components/common/VoiceInput';
import { getCampaigns, createCampaign } from '../services/api';

interface Campaign {
  id: string;
  name: string;
  description?: string;
  campaign_type: string;
  status: string;
  recipient_count?: number;
  created_at: string;
  updated_at: string;
  tags?: string[];
}

const statusColors: Record<string, string> = {
  draft: '#6b7280',
  active: '#22c55e',
  sent: '#3b82f6',
  paused: '#eab308',
  completed: '#8b5cf6',
};

const typeLabels: Record<string, string> = {
  one_time: 'One-Time',
  drip: 'Drip',
  automated: 'Automated',
  product_led_growth: 'PLG',
};

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState('one_time');
  const [formTags, setFormTags] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCampaigns();
  }, []);

  async function loadCampaigns() {
    try {
      const data = await getCampaigns();
      const items = Array.isArray(data) ? data : data?.campaigns || data?.items || [];
      setCampaigns(items);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateCampaign() {
    if (!formName.trim()) return;
    setSubmitting(true);
    try {
      const tags = formTags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);
      await createCampaign({
        name: formName.trim(),
        description: formDescription,
        campaign_type: formType,
        tags,
      });
      setShowModal(false);
      setFormName('');
      setFormDescription('');
      setFormType('one_time');
      setFormTags('');
      await loadCampaigns();
    } catch (err) {
      console.error('Failed to create campaign:', err);
    } finally {
      setSubmitting(false);
    }
  }

  const filtered = campaigns.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-enter">
      <Header title="Campaigns" subtitle={`${campaigns.length} campaigns`} />
      <div className="p-4 md:p-8">
        {/* Search bar + New Campaign button */}
        <div className="mb-6 flex items-center gap-3">
          <VoiceInput
            value={search}
            onChange={setSearch}
            placeholder="Search campaigns..."
            className="flex-1 max-w-md px-4 py-2 rounded text-sm"
            style={{
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              color: 'var(--text-primary)',
            }}
          />
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded text-sm font-semibold whitespace-nowrap"
            style={{
              background: 'var(--netrun-green)',
              color: '#000',
            }}
          >
            + New Campaign
          </button>
        </div>

        {loading ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading campaigns...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No campaigns found</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => (
              <div
                key={c.id}
                onClick={() => navigate(`/campaigns/${c.id}`)}
                className="cursor-pointer rounded-lg p-5 transition-all hover:ring-1 card-interactive"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  ['--tw-ring-color' as string]: 'var(--netrun-green)',
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-display font-semibold pr-2" style={{ color: 'var(--text-primary)' }}>
                    {c.name}
                  </h3>
                  <span
                    className="text-[10px] uppercase font-bold px-2 py-0.5 rounded whitespace-nowrap"
                    style={{ background: statusColors[c.status] || '#6b7280', color: '#fff' }}
                  >
                    {c.status}
                  </span>
                </div>

                {c.description && (
                  <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                    {c.description}
                  </p>
                )}

                <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span>{typeLabels[c.campaign_type] || c.campaign_type}</span>
                  {c.recipient_count != null && <span>{c.recipient_count} contacts</span>}
                  {c.tags && c.tags.length > 0 && (
                    <div className="flex gap-1">
                      {c.tags.slice(0, 2).map(t => (
                        <Badge key={t} label={t} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-3 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  Created {new Date(c.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Campaign Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="rounded-lg p-6 w-full max-w-md mx-4 slide-up"
            style={{
              background: '#141414',
              border: '1px solid var(--border-primary)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-display font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              New Campaign
            </h2>

            {/* Name */}
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              Name <span style={{ color: 'var(--netrun-green)' }}>*</span>
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Campaign name"
              className="w-full px-3 py-2 rounded text-sm mb-3"
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
            />

            {/* Description */}
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              Description
            </label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
              className="w-full px-3 py-2 rounded text-sm mb-3 resize-none"
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
            />

            {/* Campaign Type */}
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              Type
            </label>
            <select
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              className="w-full px-3 py-2 rounded text-sm mb-3"
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="one_time">One-Time</option>
              <option value="drip">Drip</option>
              <option value="automated">Automated</option>
              <option value="product_led_growth">Product-Led Growth</option>
            </select>

            {/* Tags */}
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              Tags
            </label>
            <input
              type="text"
              value={formTags}
              onChange={(e) => setFormTags(e.target.value)}
              placeholder="Comma-separated tags"
              className="w-full px-3 py-2 rounded text-sm mb-4"
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
            />

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded text-sm"
                style={{
                  border: '1px solid var(--border-primary)',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={submitting || !formName.trim()}
                className="px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                style={{
                  background: 'var(--netrun-green)',
                  color: '#000',
                }}
              >
                {submitting ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
